'use client';

import { useMemo, useState } from 'react';
import { ArrowRight, CircleAlert, Clock3 } from 'lucide-react';
import {
  CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { DegradationEvent } from '@/lib/urm-api';
import { groupEventsByIncident, workloadKey } from '../logic';

function dateTime(ms: number): string {
  return new Date(ms).toLocaleString();
}

function number(value: number | null | undefined, suffix = ''): string {
  return value == null ? 'Unavailable' : `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

export function EventExplorer({ events }: { events: DegradationEvent[] }) {
  const incidents = useMemo(() => groupEventsByIncident(events), [events]);
  const [selectedIncidentId, setSelectedIncidentId] = useState(() => incidents[0]?.incidentId ?? '');
  const selectedIncident = incidents.find((incident) => incident.incidentId === selectedIncidentId) ?? incidents[0];
  const [selectedEventId, setSelectedEventId] = useState(() => selectedIncident?.events[0]?.id ?? '');
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const selectedEvent = selectedIncident?.events.find((event) => event.id === selectedEventId) ?? selectedIncident?.events[0];

  const candidateRows = useMemo(() => {
    if (!selectedEvent) return [];
    const rows = new Map<string, {
      key: string;
      lag30?: { rps: number; change: number };
      lag60?: { rps: number; change: number };
      lag90?: { rps: number; change: number };
    }>();
    for (const load of selectedEvent.candidate_loads) {
      const key = workloadKey(load.workload);
      const row = rows.get(key) ?? { key };
      const value = { rps: load.rps, change: load.change_from_baseline_rps };
      if (load.lag_seconds === 30) row.lag30 = value;
      if (load.lag_seconds === 60) row.lag60 = value;
      if (load.lag_seconds === 90) row.lag90 = value;
      rows.set(key, row);
    }
    return Array.from(rows.values()).sort((a, b) => {
      const peak = (row: typeof a) => Math.max(row.lag30?.change ?? 0, row.lag60?.change ?? 0, row.lag90?.change ?? 0);
      return peak(b) - peak(a);
    });
  }, [selectedEvent]);
  const visibleCandidateRows = showAllCandidates ? candidateRows : candidateRows.slice(0, 8);
  const hasCandidateIncrease = candidateRows.some((row) =>
    Math.max(row.lag30?.change ?? 0, row.lag60?.change ?? 0, row.lag90?.change ?? 0) > 0,
  );

  const timeline = useMemo(() => selectedEvent?.timeline.map((point) => ({
    ...point,
    time: new Date(point.timestamp_ms).toLocaleTimeString(),
    candidate_load: hasCandidateIncrease
      ? point.candidate_traffic.reduce((sum, item) => sum + item.rps, 0)
      : null,
  })) ?? [], [hasCandidateIncrease, selectedEvent]);
  const limitUtilization = selectedEvent?.current_limit && selectedEvent.current_limit > 0
    ? selectedEvent.victim_request_rate / selectedEvent.current_limit * 100
    : null;

  if (!selectedEvent || !selectedIncident) {
    return <div className="rounded-lg border bg-muted/20 p-8 text-center text-sm text-muted-foreground">No degradation events met both the robust z-score and meaningful-latency thresholds in this range.</div>;
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[20rem_minmax(0,1fr)]">
      <Card className="h-fit">
        <CardHeader>
          <CardTitle>Incident periods</CardTitle>
          <CardDescription>{incidents.length} time period{incidents.length === 1 ? '' : 's'} · {events.length} latency anomaly observation{events.length === 1 ? '' : 's'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {incidents.map((incident) => (
            <button
              type="button"
              key={incident.incidentId}
              aria-pressed={incident.incidentId === selectedIncident.incidentId}
              onClick={() => {
                setSelectedIncidentId(incident.incidentId);
                setSelectedEventId(incident.events[0].id);
              }}
              className={`w-full rounded-lg border p-3 text-left transition-colors ${incident.incidentId === selectedIncident.incidentId ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
            >
              <span className="block font-mono text-xs font-semibold">{incident.incidentId}</span>
              <span className="mt-1 block text-xs text-muted-foreground">{dateTime(incident.startMs)}</span>
              <Badge variant="secondary" className="mt-2">{incident.events.length} latency observation{incident.events.length === 1 ? '' : 's'}</Badge>
            </button>
          ))}
        </CardContent>
      </Card>

      <div className="min-w-0 space-y-4">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle>{selectedIncident.incidentId}</CardTitle>
                <CardDescription>Latency observations from the same time period are grouped together; they are not independent incidents.</CardDescription>
              </div>
              <label className="w-full max-w-xl text-xs font-medium text-muted-foreground">
                Latency observation
                <select
                  aria-label="Latency observation"
                  value={selectedEvent.id}
                  onChange={(event) => setSelectedEventId(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground"
                >
                  {selectedIncident.events.map((event, index) => (
                    <option key={event.id} value={event.id}>Observation {index + 1} · {workloadKey(event.victim_workload)} · {event.victim_user}</option>
                  ))}
                </select>
              </label>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Victim" value={selectedEvent.victim_user} detail={workloadKey(selectedEvent.victim_workload)} />
              <Stat label="Event window" value={dateTime(selectedEvent.event_start_ms)} detail={`to ${dateTime(selectedEvent.event_end_ms)}`} />
              <Stat label={`Actual / expected ${selectedEvent.latency_statistic === 'mean' ? 'mean latency' : 'p95 latency'}`} value={`${number(selectedEvent.actual_latency_ms, ' ms')} / ${number(selectedEvent.expected_latency_ms, ' ms')}`} detail={selectedEvent.latency_statistic === 'mean' ? 'Temporary fallback measurement' : `Mean ${number(selectedEvent.mean_latency_ms, ' ms')}`} />
              <Stat label="Statistical deviation / victim rate" value={`${number(selectedEvent.severity_z)} z`} detail={`${number(selectedEvent.victim_request_rate, ' RPS')} · limit ${number(selectedEvent.current_limit)}${limitUtilization == null ? '' : ` (${number(limitUtilization, '%')} used)`}`} />
            </div>
            <p className="text-xs leading-5 text-muted-foreground">The z-score measures how unusual this latency is relative to this workload&apos;s usual variability. A large score in a very stable period does not necessarily mean a large user impact.</p>

            {hasCandidateIncrease ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
                <div className="flex flex-wrap items-center justify-center gap-3 font-medium"><span>Candidate workload traffic rose</span><ArrowRight className="h-4 w-4 text-primary" /><span className="inline-flex items-center gap-1"><Clock3 className="h-4 w-4" />before</span><ArrowRight className="h-4 w-4 text-primary" /><span>Latency increased</span></div>
                <p className="mt-1 text-center text-xs text-muted-foreground">Possible interference signal; timing alone does not establish causation.</p>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <div><p className="font-semibold">Latency anomaly only</p><p className="mt-1 text-xs">No qualifying workload increase was found before this slowdown, so this observation does not provide evidence of API interference.</p></div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold">Selected observation timeline</h3>
              <p className="mt-1 text-xs text-muted-foreground">Actual and expected latency use the left axis.{hasCandidateIncrease ? ' Candidate workload traffic uses the right axis.' : ''} Missing series are not plotted as zero.</p>
            </div>
            <div className="h-80" aria-label={`Event timeline for ${selectedEvent.id}`}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeline} margin={{ top: 8, right: 22, left: 2, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="latency" label={{ value: 'Latency (ms)', angle: -90, position: 'insideLeft' }} />
                  {hasCandidateIncrease && <YAxis yAxisId="load" orientation="right" label={{ value: 'Candidate traffic (RPS)', angle: 90, position: 'insideRight' }} />}
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <ReferenceLine x={new Date(selectedEvent.event_start_ms).toLocaleTimeString()} yAxisId="latency" stroke="#ef4444" strokeDasharray="4 4" label="Event" />
                  <Line yAxisId="latency" type="monotone" dataKey="victim_latency_ms" name="Victim latency (ms)" stroke="#ef4444" strokeWidth={2} connectNulls />
                  <Line yAxisId="latency" type="monotone" dataKey="expected_latency_ms" name="Expected latency (ms)" stroke="#64748b" strokeDasharray="5 4" connectNulls />
                  {hasCandidateIncrease && <Line yAxisId="load" type="monotone" dataKey="candidate_load" name="Candidate traffic (RPS)" stroke="hsl(var(--primary))" strokeWidth={2} connectNulls />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h3 className="text-sm font-semibold">Workloads that increased before this slowdown</h3>
              <p className="mb-2 mt-1 text-xs text-muted-foreground">Sorted by the largest increase over baseline. This is supporting evidence, not proof that a workload caused the slowdown.</p>
              {!hasCandidateIncrease ? <p className="text-sm text-muted-foreground">No workload showed a qualifying increase before this latency anomaly.</p> : (
                <>
                  <div className="overflow-x-auto"><table className="w-full min-w-[850px] text-sm"><thead className="border-b text-xs text-muted-foreground"><tr><th className="pb-2 text-left">Workload</th><th>30s RPS</th><th>Δ baseline</th><th>60s RPS</th><th>Δ baseline</th><th>90s RPS</th><th>Δ baseline</th></tr></thead><tbody>{visibleCandidateRows.map((row) => <tr key={row.key} className="border-b"><td className="py-2 font-mono text-xs">{row.key}</td><LoadCells load={row.lag30} /><LoadCells load={row.lag60} /><LoadCells load={row.lag90} /></tr>)}</tbody></table></div>
                  {candidateRows.length > 8 && <button type="button" className="mt-3 text-sm font-medium text-primary hover:underline" onClick={() => setShowAllCandidates((value) => !value)}>{showAllCandidates ? 'Show top 8 workloads' : `Show all ${candidateRows.length} workloads`}</button>}
                </>
              )}
            </div>

            <div>
              <h3 className="mb-1 text-sm font-semibold">Highest weighted users at that time</h3>
              <p className="mb-2 text-xs text-muted-foreground">Users with the most weighted traffic near this observation; this is not a causal attribution.</p>
              {selectedEvent.top_contributing_users.length === 0 ? <p className="text-sm text-muted-foreground">No contributing users were identified.</p> : (
                <div className="flex flex-wrap gap-2">{selectedEvent.top_contributing_users.map((user) => <Badge key={user.user} variant="secondary">{user.user}: {number(user.weighted_usage)} weighted RPS</Badge>)}</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return <div className="rounded-lg border bg-muted/20 p-3"><p className="text-xs text-muted-foreground">{label}</p><p className="mt-1 break-words text-sm font-semibold">{value}</p>{detail && <p className="mt-1 break-all text-xs text-muted-foreground">{detail}</p>}</div>;
}

function LoadCells({ load }: { load?: { rps: number; change: number } }) {
  return (
    <>
      <td className="text-center">{number(load?.rps, ' RPS')}</td>
      <td className="text-center">{load && load.change > 0 ? '+' : ''}{number(load?.change, ' RPS')}</td>
    </>
  );
}
